import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Paper, 

  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import styled from "styled-components";
import axiosInstance from "../api/axios";
import { AuthContext } from "../context/AuthContext";
import MortgageTab from "../components/UserSettings/MortgageTab";
import PersonalInfoTab from "../components/UserSettings/PersonalInfoTab";
import NotificationAlert from '../components/UserSettings/NotificationAlert';
import {
  calculateMonthlyPayment,
  getFormattedAddress,
  calculateWeightedInterest,
  updateCalculatedInterest,
  calculateTotalLoanAmount,
  calculateTotalMonthlyPayment,
  calculateTotalLoanPeriod
} from '../components/UserSettings/userSettingsUtils';




const UserSettings = () => {
  const { user, fetchUser } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    // Personal info
    firstName: "",
    lastName: "",
    gender: "",
    street: "",
    houseNumber: "",
    apartment: "",
    city: "",
    // Mortgage info
    bank: "",
    loanAmount: "",
    startDate: "",
    loanPeriod: "",
    mortgageTracks: [
      { type: "", amount: "", interest: "", period: "" }
    ],
    monthlyPayment: "",
    // Account settings
    newPassword: "",
    confirmNewPassword: "",
  });
  
  const [loading, setLoading] = useState(false);
  const [loadingMortgage, setLoadingMortgage] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [mortgageErrorMsg, setMortgageErrorMsg] = useState("");
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [mortgageData, setMortgageData] = useState(null);
  const [mortgageError, setMortgageError] = useState(null);

  // Wrap the arrays with useMemo to prevent re-creating them on every render
  const banks = useMemo(() => [
    { id: 1, name: "בנק הפועלים" },
    { id: 2, name: "בנק לאומי" },
    { id: 3, name: "בנק מזרחי טפחות" },
    { id: 4, name: "בנק דיסקונט" },
    { id: 5, name: "הבנק הבינלאומי" },
    { id: 6, name: "בנק ירושלים" },
    { id: 7, name: "בנק יהב" },
    { id: 8, name: "אחר" }
  ], []);

  const trackTypes = useMemo(() => [
    { id: 1, name: "פריים" },
    { id: 2, name: "קבועה צמודה" },
    { id: 3, name: "קבועה לא צמודה" },
    { id: 4, name: "משתנה כל 5 שנים" },
    { id: 5, name: "משתנה כל שנה" },
    { id: 14, name: "משתנה כל 3 שנים לא צמודה" },
    { id: 9, name: "אחר" }
  ], []);

  // Helper functions wrapped with useCallback
  const formatDateForInput = useCallback((dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  }, []);

  const calculateLoanPeriod = useCallback((startDate, endDate) => {
    if (!startDate || !endDate) return "";
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffYears = end.getFullYear() - start.getFullYear();
    return diffYears;
  }, []);

  // Define fetchUserMortgageData function with useCallback
  const fetchUserMortgageData = useCallback(async () => {
    if (!user) return;
    
    setLoadingMortgage(true);
    setMortgageErrorMsg("");
    setMortgageError(null);
    
    try {
      const response = await axiosInstance.get(`/users_manager/mortgage`, {params: {email: user.email}});
      if (response.data) {
        setMortgageData(response.data);
      }
    } catch (error) {
      console.error("Error fetching mortgage data:", error);
      if (error.response?.status === 404) {
        setMortgageError({ status: 404, message: "לא נמצאה משכנתא במערכת" });
      } else {
        setMortgageErrorMsg("לא ניתן לטעון את נתוני המשכנתא. אנא נסה שוב מאוחר יותר.");
        setMortgageError({ status: error.response?.status || 500, message: error.message });
      }
    } finally {
      setLoadingMortgage(false);
    }
  }, [user]);

  // Define all useEffects AFTER all functions are defined
  
  // Fetch user mortgage data when user is loaded or tab changes to mortgage
  useEffect(() => {
    if (user && activeTab === 1) {
      fetchUserMortgageData();
    }
  }, [user, activeTab, fetchUserMortgageData]);

  // When user data is available, prefill fields
  useEffect(() => {
    if (user) {
      // Split address into components if it exists
      let street = "";
      let houseNumber = "";
      let apartment = "";
      
      if (user.address) {
        // Try to parse address in format "street houseNumber, דירה apartment"
        const addressParts = user.address.split(',');
        
        // Check if there's an apartment part
        if (addressParts.length > 1 && addressParts[1].includes('דירה')) {
          apartment = addressParts[1].replace('דירה', '').trim();
          
          // Now handle the street and house number
          const mainAddressParts = addressParts[0].trim().split(' ');
          if (mainAddressParts.length > 0) {
            // Last part is likely the house number
            houseNumber = mainAddressParts.pop();
            // Rest is the street
            street = mainAddressParts.join(' ');
          }
        } else {
          // No apartment, just street and possibly house number
          const mainAddressParts = user.address.trim().split(' ');
          if (mainAddressParts.length > 0) {
            // Last part is likely the house number
            houseNumber = mainAddressParts.pop();
            // Rest is the street
            street = mainAddressParts.join(' ');
          }
        }
      }
      
      setFormData((prev) => ({
        ...prev,
        firstName: user.first_name || "",
        lastName: user.last_name || "",
        gender: user.gender || "",
        street: street,
        houseNumber: houseNumber,
        apartment: apartment,
        city: user.city || "",
      }));
    }
  }, [user]);

  // When mortgage data is loaded, update form
  useEffect(() => {
    if (mortgageData) {
      // Get the first mortgage (in case there are multiple)
      const mortgage = mortgageData.mortgages[0];
      if (mortgage) {
        // Find bank name from ID
        const bankObj = banks.find(b => b.id === mortgage.bank_id);
        
        setFormData(prev => ({
          ...prev,
          bank: bankObj ? bankObj.name : "",
          loanAmount: mortgage.amount.toString(),
          monthlyPayment: mortgage.monthly_payment.toString(),
          startDate: formatDateForInput(mortgage.valid_from),
          loanPeriod: calculateLoanPeriod(mortgage.valid_from, mortgage.valid_to).toString(),
          mortgageTracks: mortgage.tracks.map(track => {
            // Find track name from ID
            const trackTypeObj = trackTypes.find(t => t.id === track.track_id);
            
            return {
              id: track.id,
              type: trackTypeObj ? trackTypeObj.id : "",
              amount: track.amount.toString(),
              interest: track.current_loan_rate.toString(),
              period: calculateLoanPeriod(track.valid_from, track.valid_to).toString(),
              monthly_payment: track.monthly_payment.toString(),
              current_base_interest: track.current_base_interest,
              addition_rate: track.addition_rate
            };
          })
        }));
      }
    }
  }, [mortgageData, banks, trackTypes, calculateLoanPeriod, formatDateForInput]);

  // Update monthly payment when track details change
  useEffect(() => {
    // Only trigger this effect when the mortgage tab is active
    if (activeTab !== 1) return;
    
    const updatedTracks = formData.mortgageTracks.map(track => {
      // Only calculate if all required fields are filled and monthly payment is empty
      if (track.amount && track.interest && track.period && !track.monthly_payment) {
        return {
          ...track,
          monthly_payment: calculateMonthlyPayment(track.amount, track.interest, track.period)
        };
      }
      return track;
    });
    
    // Update form data if there are changes
    if (JSON.stringify(updatedTracks) !== JSON.stringify(formData.mortgageTracks)) {
      setFormData(prev => ({
        ...prev,
        mortgageTracks: updatedTracks
      }));
    }
  }, [formData.mortgageTracks, activeTab]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    
    if (e.target.name === "newPassword" || e.target.name === "confirmNewPassword") {
      const newPass = e.target.name === "newPassword" ? e.target.value : formData.newPassword;
      const confirmPass = e.target.name === "confirmNewPassword" ? e.target.value : formData.confirmNewPassword;
      setPasswordMismatch(newPass !== confirmPass && confirmPass !== "");
    }
  };

  const handleTrackChange = (index, field, value) => {
    const updatedTracks = updateCalculatedInterest(formData.mortgageTracks, index, field, value);
    setFormData({ ...formData, mortgageTracks: updatedTracks });
  };

  const addTrack = () => {
    setFormData({
      ...formData,
      mortgageTracks: [...formData.mortgageTracks, { type: "", amount: "", interest: "", period: "", monthly_payment: "", current_base_interest: "", addition_rate: "" }]
    });
  };

  const removeTrack = (index) => {
    if (formData.mortgageTracks.length > 1) {
      const updatedTracks = formData.mortgageTracks.filter((_, i) => i !== index);
      setFormData({ ...formData, mortgageTracks: updatedTracks });
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePersonalSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setSuccessMsg("");
    setErrorMsg("");
    
    try {
      // Build update payload with personal data according to API structure
      const updatePayload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        gender: formData.gender,
        address: getFormattedAddress(), // Use combined address field
        city: formData.city
      };
      
      if (formData.newPassword) {
        updatePayload.new_password = formData.newPassword;
      }
      
      // Check if user email is available
      if (!user || !user.email) {
        throw new Error("אימייל משתמש לא נמצא");
      }
      
      // Send PUT request to update user settings with email parameter
      await axiosInstance.put(`/users_manager/settings?email=${user.email}`, updatePayload);
      
      setSuccessMsg("הפרטים עודכנו בהצלחה!");
      await fetchUser(); // refresh user data in context
    } catch (err) {
      setErrorMsg("אירעה שגיאה בעדכון הפרטים. אנא נסה שוב.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleMortgageSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setSuccessMsg("");
    setErrorMsg("");
    
    try {
      // Get bank ID from name
      const selectedBank = banks.find(b => b.name === formData.bank);
      if (!selectedBank) {
        throw new Error("יש לבחור בנק תקין");
      }
      
      // Calculate end date based on the longest track period
      const startDate = new Date(formData.startDate);
      const endDate = new Date(startDate);
      const maxPeriod = calculateTotalLoanPeriod(formData.mortgageTracks);
      endDate.setFullYear(endDate.getFullYear() + maxPeriod);
      
      // Prepare mortgage data for API using calculated values
      let mortgageUpdateData = {
        bank_id: selectedBank.id,
        amount: parseFloat(calculateTotalLoanAmount(formData.mortgageTracks)),
        loan_interest: parseFloat(calculateWeightedInterest(formData.mortgageTracks) || 0),
        monthly_payment: parseFloat(calculateTotalMonthlyPayment(formData.mortgageTracks)),
        monthly_payment_predict: parseFloat(calculateTotalMonthlyPayment(formData.mortgageTracks)) * 1.1, // Estimate 10% increase
        rate_to_shekel: 1, // Default for ILS
        valid_from: startDate.toISOString().split('T')[0],
        valid_to: endDate.toISOString().split('T')[0],
        tracks: []
      };
      
      // If we have existing mortgage data, include the ID
      if (mortgageData && mortgageData.mortgages.length > 0) {
        mortgageUpdateData.id = mortgageData.mortgages[0].id;
      }
      
      // Process each track
      formData.mortgageTracks.forEach(track => {
        if (track.type && track.amount) {
          const trackStartDate = new Date(formData.startDate);
          const trackEndDate = new Date(trackStartDate);
          trackEndDate.setFullYear(trackEndDate.getFullYear() + parseInt(track.period || 0));
          
          const trackData = {
            track_id: parseInt(track.type),
            amount: parseFloat(track.amount || 0),
            monthly_payment: parseFloat(track.monthly_payment || 0),
            current_base_interest: parseFloat(track.current_base_interest || 0),
            addition_rate: parseFloat(track.addition_rate || 0),
            current_loan_rate: parseFloat(track.interest || 0),
            valid_from: trackStartDate.toISOString().split('T')[0],
            valid_to: trackEndDate.toISOString().split('T')[0],
          };
          
          // If track has ID (exists already), include it
          if (track.id) {
            trackData.id = track.id;
          }
          
          mortgageUpdateData.tracks.push(trackData);
        }
      });

      await axiosInstance.put(`/users_manager/mortgage/${user.email}`, mortgageUpdateData);
      await fetchUserMortgageData();
      setSuccessMsg("הפרטים עודכנו בהצלחה!");
      await fetchUser();
      setIsEditMode(false); // Exit edit mode after successful save
    } catch (err) {
      setErrorMsg(err.message || "אירעה שגיאה בעדכון הפרטים. אנא נסה שוב.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Toggle edit mode
  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    // If canceling edit mode, reset form data to original data
    if (isEditMode && mortgageData) {
      // Reset form data based on mortgageData
      const mortgage = mortgageData.mortgages[0];
      if (mortgage) {
        const bankObj = banks.find(b => b.id === mortgage.bank_id);
        
        setFormData(prev => ({
          ...prev,
          bank: bankObj ? bankObj.name : "",
          loanAmount: mortgage.amount.toString(),
          monthlyPayment: mortgage.monthly_payment.toString(),
          startDate: formatDateForInput(mortgage.valid_from),
          loanPeriod: calculateLoanPeriod(mortgage.valid_from, mortgage.valid_to).toString(),
          mortgageTracks: mortgage.tracks.map(track => {
            const trackTypeObj = trackTypes.find(t => t.id === track.track_id);
            
            return {
              id: track.id,
              type: trackTypeObj ? trackTypeObj.id : "",
              amount: track.amount.toString(),
              interest: track.current_loan_rate.toString(),
              period: calculateLoanPeriod(track.valid_from, track.valid_to).toString(),
              monthly_payment: track.monthly_payment.toString(),
              current_base_interest: track.current_base_interest,
              addition_rate: track.addition_rate
            };
          })
        }));
      }
    }
  };

  return (
    <Container>
      <Header>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" fontWeight="bold" sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            position: 'relative'
          }}>
            <SettingsIcon sx={{ color: theme.palette.primary.main, fontSize: '2rem' }} />
            הגדרות משתמש
           
          </Typography>
        </Box>
        <Typography variant="subtitle1" color="text.secondary" sx={{ 
          textAlign: 'center', 
          maxWidth: '600px', 
          mx: 'auto', 
          mb: 3,
          '& span': { fontWeight: 'medium', color: theme.palette.primary.main }
        }}>
          כאן תוכל לעדכן את <span>הפרטים האישיים</span> שלך ולנהל את <span>הגדרות המשכנתא</span>
        </Typography>
      </Header>
      
      {loading && (
        <LoadingIndicator>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 3 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              מעבד את הבקשה שלך...
            </Typography>
          </Box>
        </LoadingIndicator>
      )}
      
      <NotificationsContainer>
        {errorMsg && (
          <NotificationAlert
            type="error"
            message={errorMsg}
            title="שגיאה"
            onClose={() => setErrorMsg("")}
          />
        )}
        
        {successMsg && (
          <NotificationAlert
            type="success"
            message={successMsg}
            title="בוצע בהצלחה"
            onClose={() => setSuccessMsg("")}
          />
        )}

        {mortgageErrorMsg && (
          <NotificationAlert
            type="error"
            message={mortgageErrorMsg}
            onClose={() => setMortgageErrorMsg("")}
          />
        )}
      </NotificationsContainer>
      
      <StyledTabs value={activeTab} onChange={handleTabChange} centered>
        <StyledTab icon={<PersonIcon />} label="פרטים אישיים" />
        <StyledTab icon={<HomeIcon />} label="פרטי המשכנתא" />
      </StyledTabs>

      {activeTab === 0 && (
        <PersonalInfoTab
          formData={formData}
          handleChange={handleChange}
          handleSubmit={handlePersonalSubmit}
          loading={loading}
          user={user}
          passwordMismatch={passwordMismatch}
          getFormattedAddress={() => getFormattedAddress(formData.street, formData.houseNumber, formData.apartment)}
        />
      )}

      {activeTab === 1 && (
        <TabPanel>
          <MortgageTab
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleMortgageSubmit}
            loading={loading}
            loadingMortgage={loadingMortgage}
            mortgageErrorMsg={mortgageErrorMsg}
            mortgageError={mortgageError}
            isEditMode={isEditMode}
            toggleEditMode={toggleEditMode}
            banks={banks}
            trackTypes={trackTypes}
            handleTrackChange={handleTrackChange}
            addTrack={addTrack}
            removeTrack={removeTrack}
            calculateWeightedInterest={() => calculateWeightedInterest(formData.mortgageTracks)}
            isMobile={isMobile}
          />
        </TabPanel>
      )}
    </Container>
  );
};

export default UserSettings;

// STYLED COMPONENTS
const Container = styled(Paper)`
  max-width: 800px;
  margin: 40px auto;
  padding: 30px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
  direction: rtl;
  
  @media (max-width: 600px) {
    padding: 20px 16px;
    margin: 20px auto;
  }
`;

const Header = styled(Box)`
  text-align: center;
  margin-bottom: 30px;
`;

const LoadingIndicator = styled(Box)`
  display: flex;
  justify-content: center;
  margin: 20px 0;
`;





const TabPanel = styled(Box)`
  padding: 20px 0;
`;



const StyledTabs = styled(Tabs)`
  margin-bottom: 20px;
  
  & .MuiTabs-indicator {
    background-color: #1976d2;
    height: 3px;
  }
`;

const StyledTab = styled(Tab)`
  font-weight: bold;
  text-transform: none;
  font-size: 1rem;
  
  &.Mui-selected {
    color: #1976d2;
  }
`;

const NotificationsContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
`;



